/**
 * The capability manifest: one picture of what RecordOS can make, for every
 * seat that drafts or changes a setup. Generated, not written: the ids come
 * from the code's own lists (`ids.ts`), each entry's options and limits are
 * read from the constants the code enforces (`capabilityParts`), and the one
 * hand-written half, the words (`words.ts`), is a `Record<CapabilityId, …>`
 * the compiler keeps complete.
 *
 * `buildManifest` is the one way in. The server adds the verbs half (the tool
 * schemas and the rights behind them) in apps/server/src/agent/harness/manifest.ts.
 */
import { AGENT_FILE_EXTENSIONS, AGENT_FILE_MAX_BYTES, AGENT_FILES_AT_ONCE, fileKindOf } from '../agent-files';
import { LINES_MAX_DEPTH, LINES_MAX_FIELDS, LINES_MAX_PER_ENTRY, RECORD_ICON_IDS } from '../api';
import { COMPOSE_LIMITS, UNMET_NEED_LEVELS } from '../compose';
import {
  SETUP_AGGS,
  SETUP_DIRECTIONS,
  SETUP_DRAFT_LIMITS,
  SETUP_FORMULA_AGG_OPS,
  SETUP_FORMULA_CALC_OPS,
  SETUP_FORMULA_TEST_OPS,
  SETUP_LINK_OPS,
  SETUP_TILE_OPS,
  SETUP_TILE_SCOPES,
  type SetupDraftLink,
  type SetupDraftRecord,
} from '../compose-author';
import { ENTRY_SLOT_COUNT } from '../entry-slots';
import { SHAPES_FOR } from '../field-shapes';
import { CHOICE_MAX_CHOSEN, FORMULA_MAX_DEPTH, FORMULA_MAX_NODES } from '../formula';
import { MANIFEST_VERSION } from '../generated/manifest-version';
import { VALUE_TYPE_IDS } from '../generated/value-types';
import { IMPORT_MODES } from '../imports';
import { INSIGHT_AGG_MAX_DEPTH, INSIGHT_AGG_MAX_LEAVES, INSIGHT_WHERE_OPS } from '../insights';
import { LAYOUT_NODES_MAX, LAYOUT_SECTION_DEPTH_MAX } from '../layout';
import { ACCESS_LEVELS, INVITE_DELIVERIES, PERMISSIONS, REACHES } from '../roles';
import type { StudioDraft } from '../setup-draft/draft';
import { WEBSITE_MAX_PAGES } from '../websites';
import { CAPABILITY_IDS, levelOf, type CapabilityId, type CapabilityLevel, type CapabilityStatus } from './ids';
import { CAPABILITY_WORDS } from './words';

/* --------------------------------------------------------------- the types */

export type CapabilityReach = {
  /** What a setup draft can build today, as `authorProblems` judges it. */
  readonly setup: 'yes' | 'no';
  /** What Edit the setup can change today. */
  readonly edit: 'yes' | 'no';
  /** The OS tools that make it in a workspace (`AgentToolName` strings; the server checks them). */
  readonly os: readonly string[];
  /** Whether an app screen makes it. */
  readonly screen: boolean;
};

/** A part of a setup draft: the records it adds and the links between them. */
export type SetupDraftFragment = {
  readonly records: readonly SetupDraftRecord[];
  readonly links?: readonly SetupDraftLink[];
};

/**
 * A part of a studio draft: any of its lists, merged into an empty draft to be
 * judged. Its records are the builder's (no card in an example needs a key).
 */
export type StudioDraftFragment = Partial<Pick<StudioDraft, 'links' | 'boardCards' | 'roles' | 'imports' | 'sources' | 'notes' | 'notYet'>> & {
  readonly records?: readonly SetupDraftRecord[];
};

export type CapabilityWords = {
  /** The plain name: "Lines". */
  readonly name: string;
  /** One line: "Many rows inside one entry, each with its own values". */
  readonly what: string;
  /** What a business uses it for: "The medicines on a bill". */
  readonly use: string;
  /** The question that reveals the need: "Does one bill have many medicines?" */
  readonly ask?: string;
  /** Words a person says for it, Hinglish included. */
  readonly also?: readonly string[];
  readonly status: CapabilityStatus;
  readonly reach: CapabilityReach;
  /** A draft part that builds it. Present on every `reach.setup: 'yes'` entry. */
  readonly example?: SetupDraftFragment;
  /**
   * A studio draft part that builds it, for what a `SetupDraft` cannot hold
   * (OR groups, cards across records, roles, imports). Judged by
   * `studioProblems`; present on every `reach.setup: 'yes'` entry that has no
   * `example`.
   */
  readonly studioExample?: StudioDraftFragment;
  /** A draft part that tries it and must be refused. */
  readonly attempt?: SetupDraftFragment;
  /** Why no draft can even try it. */
  readonly unexpressible?: string;
  /** What the person asked for, and the closest fit made from parts that exist. On every entry not shipped. */
  readonly cannotYet?: { readonly need: string; readonly fit: string };
};

export type CapabilityEntry = CapabilityWords & {
  readonly id: CapabilityId;
  readonly level: CapabilityLevel;
  /** From code: the enum members. */
  readonly options?: readonly string[];
  /** From code: the constants, by name. */
  readonly limits?: Readonly<Record<string, number>>;
  /** The file and symbol the entry is read from. */
  readonly source: string;
};

export type CapabilityManifest = { readonly version: string; readonly entries: readonly CapabilityEntry[] };

/* --------------------------------------------------------------- the parts */

type CapabilityPart = Omit<CapabilityEntry, keyof CapabilityWords>;

const S = 'packages/shared/src';

/** The file and symbol each family is read from, by id prefix (longest first wins). */
const SOURCE_OF: ReadonlyArray<readonly [string, string]> = [
  ['field.type.', `${S}/generated/value-types.ts#VALUE_TYPE_IDS`],
  ['field.option.', `${S}/manifest/ids.ts#FIELD_OPTION_KEYS`],
  ['record.', `${S}/manifest/ids.ts#RECORD_KEYS`],
  ['formula.piece.', `${S}/formula.ts#FORMULA_NODE_KINDS`],
  ['surface.', `${S}/manifest/ids.ts#SURFACE_KEYS`],
  ['arrangement.', `${S}/manifest/ids.ts#ARRANGEMENT_KEYS`],
  ['lifecycle.', `${S}/manifest/ids.ts#LIFECYCLE_KEYS`],
  ['view.', `${S}/manifest/ids.ts#VIEW_KEYS`],
  ['insight.card.', `${S}/insights.ts#INSIGHT_CARD_IDS`],
  ['insight.', `${S}/manifest/ids.ts#INSIGHT_KEYS`],
  ['link.pattern.', `${S}/links.ts#LINK_PATTERNS`],
  ['link.mode.', `${S}/links.ts#LINK_MODES`],
  ['link.fill.', `${S}/links.ts#LINK_MAPPING_KINDS`],
  ['link.', `${S}/manifest/ids.ts#LINK_KEYS`],
  ['people.group.', `${S}/roles.ts#PERMISSION_GROUPS`],
  ['people.', `${S}/manifest/ids.ts#PEOPLE_KEYS`],
  ['memory.', `${S}/manifest/ids.ts#MEMORY_KEYS`],
  ['input.file.', `${S}/agent-files.ts#FILE_KINDS`],
  ['input.', `${S}/manifest/ids.ts#INPUT_KEYS`],
  ['workspace.', `${S}/manifest/ids.ts#WORKSPACE_KEYS`],
  ['not_yet.', `${S}/manifest/ids.ts#NOT_YET_LEVEL`],
];

const sourceOf = (id: CapabilityId): string => SOURCE_OF.find(([prefix]) => id.startsWith(prefix))?.[1] ?? S;

const L = SETUP_DRAFT_LIMITS;

/** Options and limits for the ids whose code holds them; every other id has neither. */
const PARTS: Partial<Record<CapabilityId, { options?: readonly string[]; limits?: Readonly<Record<string, number>> }>> = {
  'field.type.select': { limits: { optionsMin: L.optionsMin, options: L.options } },
  'field.type.multi_select': { limits: { maxChosen: CHOICE_MAX_CHOSEN } },
  'field.type.lines': { limits: { fields: LINES_MAX_FIELDS, depth: LINES_MAX_DEPTH, perEntry: LINES_MAX_PER_ENTRY } },
  'formula.piece.calc': { options: SETUP_FORMULA_CALC_OPS },
  'formula.piece.if': { options: SETUP_FORMULA_TEST_OPS },
  'formula.piece.agg': { options: SETUP_FORMULA_AGG_OPS },
  'record.record': { limits: { records: COMPOSE_LIMITS.records, fields: COMPOSE_LIMITS.fields, ...ENTRY_SLOT_COUNT } },
  'record.direction': { options: SETUP_DIRECTIONS },
  'record.icon': { options: RECORD_ICON_IDS },
  'record.seed_rows': { limits: { rows: L.seedRows, values: L.seedValues, seedRows: COMPOSE_LIMITS.seedRows } },
  'surface.sheet': { limits: { sharedFields: L.sheetSharedFields } },
  'arrangement.sections': {
    limits: {
      sections: L.sections,
      innerSections: L.innerSections,
      sectionFields: L.sectionFields,
      depth: LAYOUT_SECTION_DEPTH_MAX,
      nodes: LAYOUT_NODES_MAX,
    },
  },
  'insight.agg': { options: SETUP_AGGS },
  'insight.scope': { options: SETUP_TILE_SCOPES },
  'insight.where': { options: SETUP_TILE_OPS, limits: { where: L.tileWhere } },
  'insight.where_list': { options: INSIGHT_WHERE_OPS },
  'insight.cross_record': { limits: { leaves: INSIGHT_AGG_MAX_LEAVES, depth: INSIGHT_AGG_MAX_DEPTH } },
  'view.summary': { limits: { cards: L.tilesPerRecord } },
  'link.and': { options: SETUP_LINK_OPS, limits: { when: L.linkWhen, carry: L.linkCarry, links: COMPOSE_LIMITS.links } },
  'people.role': { options: REACHES },
  'people.access': { options: ACCESS_LEVELS },
  'people.invite': { options: INVITE_DELIVERIES },
  'memory.website': { limits: { pages: WEBSITE_MAX_PAGES } },
  'memory.unmet_need': { options: UNMET_NEED_LEVELS, limits: { perSetup: COMPOSE_LIMITS.unmetNeeds } },
  'input.import': { options: IMPORT_MODES },
  'workspace.name': { limits: { chars: COMPOSE_LIMITS.businessName } },
};

const FORMULA_LIMITS = { depth: FORMULA_MAX_DEPTH, nodes: FORMULA_MAX_NODES } as const;
const FILE_LIMITS = { maxBytes: AGENT_FILE_MAX_BYTES, atOnce: AGENT_FILES_AT_ONCE } as const;

/** Per id, the options and limits read from code, never typed by hand. */
export const capabilityParts = (): readonly CapabilityPart[] =>
  CAPABILITY_IDS.map((id): CapabilityPart => {
    const own = PARTS[id] ?? {};
    let options = own.options;
    let limits = own.limits;
    const [root, family, member] = id.split('.') as [string, string, string | undefined];
    if (root === 'field' && family === 'type' && member !== undefined && (VALUE_TYPE_IDS as readonly string[]).includes(member)) {
      options = SHAPES_FOR[member as (typeof VALUE_TYPE_IDS)[number]].map((shape) => shape.id);
    }
    if (root === 'formula' && family === 'piece') limits = { ...FORMULA_LIMITS, ...limits };
    if (root === 'people' && family === 'group' && member !== undefined) {
      options = PERMISSIONS.filter((permission) => permission.group === member).map((permission) => permission.key);
    }
    if (root === 'input' && family === 'file' && member !== undefined) {
      // An `.xml` names the generic XML kind; the commit's sniff reads one with a Tally envelope as a Tally export.
      options = AGENT_FILE_EXTENSIONS.filter((extension) => {
        const kind = fileKindOf(`file${extension}`, '');
        return kind === member || (member === 'tally' && kind === 'xml');
      });
      limits = FILE_LIMITS;
    }
    return {
      id,
      level: levelOf(id),
      ...(options === undefined ? {} : { options }),
      ...(limits === undefined ? {} : { limits }),
      source: sourceOf(id),
    };
  });

/* ------------------------------------------------------------ the manifest */

const freeze = <T>(value: T): T => {
  if (typeof value === 'object' && value !== null && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const inner of Object.values(value)) freeze(inner);
  }
  return value;
};

/** Every entry, parts and words merged, in `CAPABILITY_IDS` order. Without the version. */
export const manifestEntries = (): readonly CapabilityEntry[] =>
  capabilityParts().map((part) => ({ ...CAPABILITY_WORDS[part.id], ...part }));

/**
 * JSON with every object's keys sorted and arrays in order, `undefined`
 * dropped as JSON drops it: the bytes a manifest version is hashed over, the
 * same on every machine.
 */
export const canonicalJson = (value: unknown): string => {
  if (Array.isArray(value)) return `[${value.map((one) => (one === undefined ? 'null' : canonicalJson(one))).join(',')}]`;
  if (typeof value === 'object' && value !== null) {
    const keys = Object.keys(value).sort();
    const parts = keys
      .filter((key) => (value as Record<string, unknown>)[key] !== undefined)
      .map((key) => `${JSON.stringify(key)}:${canonicalJson((value as Record<string, unknown>)[key])}`);
    return `{${parts.join(',')}}`;
  }
  return JSON.stringify(value);
};

let built: CapabilityManifest | null = null;

/** The manifest, frozen and built once. */
export const buildManifest = (): CapabilityManifest => {
  built ??= freeze({ version: MANIFEST_VERSION, entries: structuredClone(manifestEntries()) });
  return built;
};

const BY_ID = (): ReadonlyMap<string, CapabilityEntry> => new Map(buildManifest().entries.map((entry) => [entry.id, entry]));
let byId: ReadonlyMap<string, CapabilityEntry> | null = null;

export const isCapabilityId = (value: unknown): value is CapabilityId =>
  typeof value === 'string' && (CAPABILITY_IDS as readonly string[]).includes(value);

export const capabilityById = (id: string): CapabilityEntry | null => {
  byId ??= BY_ID();
  return byId.get(id) ?? null;
};

/* ------------------------------------------------------------- the lookup */

/** Words too common to tell one entry from another, English and Hinglish. */
const STOP = new Set(['a', 'an', 'the', 'to', 'of', 'and', 'or', 'for', 'my', 'is', 'are', 'it', 'this', 'that', 'ka', 'ki', 'ke', 'hai', 'mein']);

/** Lower-cased words: letters (Devanagari included) and digits, the stop words dropped. */
const wordsOf = (text: string): string[] =>
  text
    .toLowerCase()
    .split(/[^\p{L}\p{M}\p{N}]+/u)
    .filter((word) => word !== '' && !STOP.has(word));

/** Whole-word hits of the need's words in a text. */
const hits = (need: ReadonlySet<string>, text: string | undefined): number =>
  text === undefined ? 0 : wordsOf(text).filter((word) => need.has(word)).length;

/**
 * The entries that best answer a need in a person's words: name and `also`
 * weigh three, the cannot-yet need and `what` two, `use` and `ask` one, by
 * whole-word hits. Ties keep the manifest's order; an entry with no hit is
 * left out.
 */
export const findCapabilities = (need: string, limit = 4): CapabilityEntry[] => {
  const asked = new Set(wordsOf(need));
  if (asked.size === 0) return [];
  return buildManifest()
    .entries.map((entry, order) => ({
      entry,
      order,
      score:
        3 * hits(asked, entry.name) +
        3 * (entry.also ?? []).reduce((sum, word) => sum + hits(asked, word), 0) +
        2 * hits(asked, entry.cannotYet?.need) +
        2 * hits(asked, entry.what) +
        hits(asked, entry.use) +
        hits(asked, entry.ask),
    }))
    .filter((scored) => scored.score > 0)
    .sort((a, b) => b.score - a.score || a.order - b.order)
    .slice(0, limit)
    .map((scored) => scored.entry);
};

/** The entries for some ids, and the ids that name none. */
export const describeCapabilities = (ids: readonly string[]): { entries: CapabilityEntry[]; unknown: string[] } => {
  const entries: CapabilityEntry[] = [];
  const unknown: string[] = [];
  for (const id of ids) {
    const entry = capabilityById(id);
    if (entry === null) unknown.push(id);
    else entries.push(entry);
  }
  return { entries, unknown };
};

/** A draft fragment as short lines a model reads: each record with its fields, each link with its conditions. */
export const compactFragment = (fragment: SetupDraftFragment): string[] => {
  const fieldLine = (field: SetupDraftRecord['fields'][number]): string => {
    const extra = [
      field.targetRecordKey === undefined ? null : `→ ${field.targetRecordKey}`,
      field.options === undefined ? null : `[${field.options.map((option) => option.key).join('|')}]`,
      field.lines === undefined ? null : `{${field.lines.fields.map(fieldLine).join(', ')}}`,
      field.formula === undefined ? null : `= ${field.formula.expr.kind}`,
    ].filter((part) => part !== null);
    return [`${field.key}:${field.type}`, ...extra].join(' ');
  };
  return [
    ...fragment.records.map(
      (record) =>
        `record ${record.key} "${record.name}" (${record.kind}, ${record.direction}${record.capture === undefined ? '' : ', sheet'}): ${record.fields.map(fieldLine).join(', ')}${
          record.tiles === undefined || record.tiles.length === 0 ? '' : `; cards ${record.tiles.map((tile) => `${tile.card}:${tile.agg}`).join(', ')}`
        }`,
    ),
    ...(fragment.links ?? []).map(
      (link) =>
        `link ${link.fromRecordKey} → ${link.toRecordKey} (${link.pattern}) when ${link.when.length === 0 ? 'always' : link.when.map((one) => `${one.fieldKey} ${one.op}${one.value === undefined ? '' : ` ${String(one.value)}`}`).join(' and ')}; carry ${link.carry.map((one) => one.toFieldKey).join(', ')}`,
    ),
  ];
};
